<template>
  <article class="pricing">
    <div class="grid grid--container">
      <VText variant="h1" style="margin-bottom: 0.25rem"
        >Simple, Transparent Pricing</VText
      >
      <VText class="pricing__copy" gutter-bottom
        >Whether you're a creator, solo-entrepreneur, small business, or agency,
        we've got you covered. Start any of our plans with a 10-day trial today
        and cancel anytime.</VText
      >

      <Tabs v-model="selectedTab" alwaysRenderTabContent>
        <TabBar aria-label="Pricing options" style="margin-bottom: 2rem">
          <Tab value="personal">Personal</Tab>
          <Tab value="organizations">Organizations</Tab>
        </TabBar>

        <TabPanel value="personal">
          <PricingPlans />
        </TabPanel>
        <TabPanel value="organizations">
          <OrgPlans />
        </TabPanel>
      </Tabs>

      <div class="pricing__compare-link">
        <InlineLink href="#compare"
          ><span>Compare our plans features <ChevronIcon size=".8em" /></span
        ></InlineLink>
      </div>

      <div class="row row--md-h-center">
        <div class="col col--lg-8">
          <Card class="inset" :hideStaticShadow="false" color="gray100">
            <CardText>
              <h2 class="h3">Discount for Non-profits</h2>
              <p>
                At Campsite.bio, we're proud to support teams doing great
                things. Reach out at
                <a href="mailto:support@campsite.bio">support@campsite.bio</a>
                to apply for a discount on any plan. We'll need proof of
                non-profit status, like an IRS determination letter.
              </p>
            </CardText>
          </Card>
        </div>
      </div>

      <PricingCompare id="compare" />

      <section class="pricing__faq">
        <VText variant="h2" style="margin-bottom: 1rem"
          >Frequently Asked Questions</VText
        >
        <div class="row">
          <div class="col col--lg-8">
            <AccordionItemGroup>
              <AccordionItem title="Why do I need a Campsite.bio?">
                <p>
                  Campsite.bio is a single link that lets you connect followers
                  to all of your content, everywhere. It's the only link you'll
                  ever need to share. It enables you to share many links in a
                  single, consolidated landing page. It makes it easy for your
                  followers and customers to find you and your content.
                </p>

                <p>
                  You can add your bio link on social media like Instagram or
                  TikTok, include it on your email signature, or share it via a
                  QR code.
                </p>
              </AccordionItem>
              <AccordionItem title="What if I need more profiles/accounts?">
                <p>
                  Support for multiple profiles is built right into our product.
                  We make it easy to manage them from a single account with a
                  single login. Some plans include multiple profiles, and you
                  can easily add more profiles to any plan for $1.50 per month
                  or $15 per year per profile.
                </p>
              </AccordionItem>
              <AccordionItem
                title="What is an organization? How is it different from a personal plan?"
              >
                <p>
                  Organizations are shared accounts where businesses can manage
                  multiple profiles at once, with easy member management and
                  sophisticated administrative features.
                </p>
                <p>
                  Your team can work together to manage the many profiles by
                  using an organization account. It serves as a container for
                  your shared work and gives the work a unique name and brand.
                </p>
                <p>
                  Each person that joins Campsite.bio has their own personal
                  account, and multiple personal accounts can join together by
                  joining the same organization. A subset of these personal
                  accounts can be given the role of organization owner, which
                  allows those people to granularly manage access to the
                  organization's profiles using the administrative features.
                </p>
              </AccordionItem>
              <AccordionItem
                title="What is the difference between a collaborator and a member?"
              >
                <h3>Collaborator</h3>
                <p>
                  Available on our Pro and Pro+ plans, collaborators are people
                  who you give access to a single Campsite.bio profile. They
                  only have access to the profile you directly add them to.
                </p>

                <h3>Member</h3>
                <p>
                  Available on our Organization plans, members are people who
                  you give access to an entire organization. Members can be
                  given different roles, which allow them to perform different
                  actions within the organization.
                </p>
                <p>
                  Members can be given access to all of your profiles, or the
                  specific ones they've been added to.
                </p>
              </AccordionItem>
              <AccordionItem title="What is a custom domain?">
                <p>
                  A Custom Domain is a domain name (www.mybrand.com) you
                  independently buy from a 3rd party domain registrar. They cost
                  on average $10-$15 a year, depending on the seller and
                  estimated domain name value.
                </p>
                <p>
                  You can use your purchased custom domain on Campsite.bio and
                  share it with your followers (ex. links.mybrand.com). This is
                  powerful in that it allows you to present a link that your
                  followers will be familiar with and trust.
                </p>
              </AccordionItem>
              <AccordionItem
                title="What sort of analytics do I get with Pro and Pro+?"
              >
                <h3>Pro</h3>
                <p>
                  Campsite.bio Pro unlocks access to detailed analytics like
                  page views and clicks breakdowns, along with referral,
                  country, device, and event charts. You can view up to 60 days
                  of data with Pro.
                </p>
                <p>
                  For your links, you can view a day-by-day breakdown of clicks,
                  for up to 28 days. You'll also be able to see top referrers,
                  countries, and device types.
                </p>

                <h3>Pro+ / Analytics add-on</h3>
                <p>
                  Our Analytics add-on, which is available to be added to the
                  Pro subscription, or built into the Pro+ subscription, unlocks
                  even more insights. Get lifetime analytics storage, five
                  different reports, data filtering capabilities, and the
                  ability to download your analytics to a .csv.
                </p>
              </AccordionItem>
              <AccordionItem
                title="How can I drive more traffic through my Campsite.bio profile?"
              >
                <p>
                  Make sure to share your Campsite.bio profile link everywhere
                  you can. Add it to your social media profiles, email
                  signature, and website. You can also share it via a QR code.
                </p>
                <p>
                  Once visitors land on your Campsite.bio profile, gain valuable
                  insights with user-friendly analytics to understand their
                  origin and click behavior. Identify what's effective and what
                  needs improvement, allowing you to optimize your Campsite.bio
                  in real time. Experiment with link placement, prioritize
                  important links, utilize subheadings, add animation, and more,
                  ensuring your traffic is directed precisely where you want it
                  to go.
                </p>
              </AccordionItem>
            </AccordionItemGroup>
            <p style="margin-top: 1.875rem">
              Don't see an answer to your question?
              <a href="https://support.campsite.bio/en/collections/3754648-faqs"
                >Check out our FAQ page</a
              >.
            </p>
          </div>
        </div>
      </section>
    </div>
    <RaisedCtaSection
      title="Simplify your link in bio management today"
      text="Start taking your link in bio seriously, with a professional looking Campsite.bio profile."
    >
      <Button
        href="https://app.campsite.bio/create-account"
        variant="hard-shadow"
        size="large"
        >Get started</Button
      >
    </RaisedCtaSection>
  </article>
</template>

<script>
import {
  VText,
  InlineLink,
  ChevronIcon,
  Card,
  CardText,
  Tabs,
  Tab,
  TabBar,
  TabPanel,
  Button,
  AccordionItem,
  AccordionItemGroup,
} from '@campsite-bio/component-lib';

import { generateMeta } from '@/utils/generate-meta';
import PricingPlans from '@/components/pricing/pricing-plans';
import OrgPlans from '@/components/pricing/org-plans';
import PricingCompare from '@/components/pricing/pricing-compare';
import RaisedCtaSection from '@/components/sections/raised-cta-section';
import { useRouteQuery } from '@/compositions';

export default {
  head() {
    return generateMeta({
      title: 'Campsite.bio Pricing and Features - Free or just $7 a month',
      description:
        'Compare our free forever plan with our paid plan for Brands, Agencies, and Marketing Teams. Upgrade your bio link now.',
    });
  },

  components: {
    VText,
    InlineLink,
    ChevronIcon,
    AccordionItem,
    AccordionItemGroup,
    PricingPlans,
    PricingCompare,
    Button,
    Card,
    CardText,
    Tabs,
    Tab,
    TabBar,
    TabPanel,
    OrgPlans,
    RaisedCtaSection,
  },

  setup() {
    const selectedTab = useRouteQuery('plans', 'personal');

    return {
      selectedTab,
    };
  },
};
</script>

<style lang="scss" scoped>
.pricing {
  background: #f7f7f7;
  height: 100%;

  .grid {
    padding-top: 1rem;

    @media (min-width: 768px) {
      padding-top: 2.5rem;
    }
  }

  &__copy {
    max-width: 700px;
  }
}

.pricing__compare-link {
  margin-top: 2rem;
  text-align: center;
}

.setup-section {
  margin-top: 3.125rem;
}

.pricing__faq {
  border-top: 1px solid #cfd5e1;
  padding-top: 2.5rem;
  margin-top: 2.375rem;

  @media (min-width: 768px) {
    margin-top: 4.375rem;
  }
}

.inset {
  margin: 2rem 0;
}
</style>
